// ============================================================================
//  ProductCard — premium product-card tile for the Guest Dashboard grid.
//
//  Per the user brief (May 2026):
//    "A cinematic event control surface — NOT a colourful feature menu."
//
//  Replaces the previous full-width gradient pill launchers with a
//  modular 2-col-on-mobile / 3-4-col-on-desktop tile system. Each card
//  is a dark glass surface with a SUBTLE per-product accent glow —
//  never a neon fill, never a saturated block. Reads as a curated
//  catalogue of premium experiences, not an app utility menu.
//
//  Authored to /app/brand/originals.md:
//    • Georgia serif title
//    • Restrained glow language (per-product accent at ≤55% opacity)
//    • 24px radius, 20px internal padding, 16px grid gap
//    • Spring lift on hover/tap, max scale 1.02 (no arcade bounce)
//    • Anti-dashboard rule: no charts, no admin chrome
//
//  Props:
//    title     — string, serif headline (e.g. "Pocket Booth")
//    subtitle  — string, one-line emotional descriptor
//    icon      — React component (Phosphor or lucide icon)
//    accent    — semantic key: 'pink' | 'teal' | 'cyan' | 'violet'
//                | 'gold' | 'amber'
//    onClick   — handler
//    meta      — optional small line ("17 moments", "Live now", etc.)
//    featured  — bool; when true, renders a single restrained NEW pip
//                (per brief: max 1-2 visible at once, only for genuinely
//                new launch surfaces)
//    testId    — data-testid suffix
// ============================================================================
import React from 'react';
import { motion } from 'framer-motion';

const ACCENT = {
  pink:   { color: '#FF6BCB', glow: 'rgba(255,107,203,0.28)', icon: '#FF6BCB' },
  teal:   { color: '#C29D52', glow: 'rgba(194,157,82,0.28)',  icon: '#C29D52' },
  cyan:   { color: '#3FA7FF', glow: 'rgba(63,167,255,0.28)',  icon: '#3FA7FF' },
  violet: { color: '#B89762', glow: 'rgba(184,151,98,0.30)',  icon: '#A78BFA' },
  gold:   { color: '#D4AF5E', glow: 'rgba(212,175,94,0.30)',  icon: '#D4AF5E' },
  amber:  { color: '#FFB84D', glow: 'rgba(255,184,77,0.28)',  icon: '#FFB84D' },
};
const DEFAULT = ACCENT.gold;

export const ProductCard = ({
  title,
  subtitle,
  icon: Icon,
  accent = 'gold',
  onClick,
  meta,
  featured = false,
  testId,
}) => {
  const a = ACCENT[accent] || DEFAULT;
  return (
    <motion.button
      type="button"
      data-testid={testId || 'product-card'}
      onClick={onClick}
      whileHover={{ y: -2, scale: 1.02 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: 'spring', stiffness: 350, damping: 22 }}
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, minHeight: 168, height: '100%', padding: 20,
        borderRadius: 24,
        background: 'linear-gradient(180deg, rgba(24,24,38,0.96) 0%, rgba(16,16,26,0.96) 100%)',
        border: `1px solid ${a.color}1f`,            // ~12% accent edge — barely there
        boxShadow: `0 10px 26px rgba(0,0,0,0.40), inset 0 0 0 1px rgba(255,255,255,0.02), 0 0 22px ${a.glow}`,
        textAlign: 'left', color: 'inherit', cursor: 'pointer',
        backdropFilter: 'blur(14px)',
        overflow: 'hidden',
      }}
    >
      {/* Per-product accent veil — extremely subtle radial glow in the
          top-left corner of the card. Adds depth without ever reading
          as a "coloured block". */}
      <span
        aria-hidden
        style={{
          position: 'absolute', top: -40, left: -40,
          width: 120, height: 120, borderRadius: '50%',
          background: `radial-gradient(circle, ${a.color}26 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Featured pip — restrained, max 1-2 visible per page per brand
          rule. Renders only when explicitly requested. */}
      {featured && (
        <span
          data-testid={`${testId || 'product-card'}-featured-pip`}
          style={{
            position: 'absolute', top: 12, right: 12,
            padding: '3px 8px', borderRadius: 999,
            background: 'rgba(212,175,94,0.16)',
            border: '1px solid rgba(212,175,94,0.55)',
            color: '#D4AF5E', fontSize: 9, fontWeight: 800,
            letterSpacing: '0.18em', textTransform: 'uppercase',
          }}
        >
          new
        </span>
      )}

      {/* Icon — accent-tinted, soft glow */}
      {Icon ? (
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40, borderRadius: 12,
            background: `${a.color}14`,
            border: `1px solid ${a.color}33`,
            color: a.icon,
            filter: `drop-shadow(0 0 14px ${a.glow})`,
          }}
        >
          <Icon size={22} weight="bold" />
        </span>
      ) : null}

      {/* Title + subtitle */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
        <span
          data-testid={`${testId || 'product-card'}-title`}
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 17, fontWeight: 500, color: '#FFFFFF',
            letterSpacing: '-0.015em', lineHeight: 1.15,
          }}
        >
          {title}
        </span>
        {subtitle ? (
          <span
            style={{
              fontSize: 12, fontWeight: 500,
              color: 'rgba(255,255,255,0.55)',
              letterSpacing: '-0.005em', lineHeight: 1.3,
            }}
          >
            {subtitle}
          </span>
        ) : null}
        {meta ? (
          <span
            data-testid={`${testId || 'product-card'}-meta`}
            style={{
              marginTop: 6, fontSize: 10, fontWeight: 700,
              color: a.color, letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            {meta}
          </span>
        ) : null}
      </div>
    </motion.button>
  );
};

export default ProductCard;
